/**
 * /me/expense-claims — the employee self-service expense screen (ADR-0020).
 * Submit a reimbursement claim, attach receipts (while draft/pending), submit it
 * for approval, cancel it pre-approval, and track its two-stage lifecycle +
 * reimbursement state (awaiting / reimbursed via payroll / paid off-cycle).
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrencyCode } from '@kora/types';
import { meLayoutRoute } from './meLayout.js';
import {
  listMyClaims,
  createClaim,
  submitClaim,
  cancelClaim,
  EXPENSE_CATEGORIES,
  type ExpenseClaim,
} from '../api/expense.js';
import { Alert } from '../components/Alert.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { formatDate, formatMoney } from '../lib/format.js';
import { ClaimReceipts, StatusPill, reimbursementLabel, isEditable } from './expenseShared.js';

export const myExpenseClaimsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/expense-claims',
  component: MyExpenseClaimsPage,
});

const CURRENCIES = ['NGN', 'GHS', 'CDF', 'USD'] as const;

const FormSchema = z.object({
  amount: z
    .string()
    .regex(/^\d+(\.\d{1,4})?$/, 'Enter an amount, e.g. 80.00')
    .refine((v) => Number(v) > 0, 'Must be greater than 0'),
  currency: z.enum(CURRENCIES),
  category: z.enum(['travel', 'meals', 'accommodation', 'supplies', 'communications', 'other']),
  description: z.string().min(1, 'Required').max(1000),
  incurredDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
});
type FormValues = z.infer<typeof FormSchema>;

function MyExpenseClaimsPage(): JSX.Element {
  const query = useQuery({ queryKey: ['me', 'expense-claims'], queryFn: listMyClaims });
  const claims = query.data?.claims ?? [];

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <div className="text-xs text-gray-500">
        <Link to="/me" className="hover:text-white">
          My profile
        </Link>{' '}
        / Expense claims
      </div>
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Expense claims</h1>
        <p className="text-sm text-gray-400">
          Claim a business cost you fronted. Attach the receipt, then submit — your
          manager approves it, finance signs off, and it's reimbursed on your next
          payslip (or settled off-cycle by finance).
        </p>
      </header>

      <ClaimForm />

      <div className="space-y-4">
        <h2 className="text-lg text-white">Your claims</h2>
        {query.isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : query.isError ? (
          <Alert variant="error">Could not load your claims.</Alert>
        ) : claims.length === 0 ? (
          <p className="text-sm text-gray-600">No claims yet.</p>
        ) : (
          <div className="space-y-3">
            {claims.map((c) => (
              <ClaimRow key={c.id} claim={c} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ClaimForm(): JSX.Element {
  const queryClient = useQueryClient();
  const [success, setSuccess] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { amount: '', currency: 'NGN', category: 'travel', description: '', incurredDate: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => createClaim(values),
    onSuccess: async () => {
      reset();
      setSuccess(true);
      await queryClient.invalidateQueries({ queryKey: ['me', 'expense-claims'] });
    },
  });

  const onSubmit = (values: FormValues): void => {
    setSuccess(false);
    mutation.mutate(values);
  };

  const inputClass =
    'w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm focus:border-white focus:outline-none';

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="rounded border border-gray-900 p-4 space-y-3">
      <h2 className="text-lg text-white">New claim</h2>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="amount" className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            Amount
          </label>
          <input id="amount" {...register('amount')} className={inputClass} placeholder="80.00" />
          {errors.amount ? <span className="text-xs text-red-400">{errors.amount.message}</span> : null}
        </div>
        <div>
          <label htmlFor="currency" className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            Currency
          </label>
          <select id="currency" {...register('currency')} className={inputClass}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="category" className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            Category
          </label>
          <select id="category" {...register('category')} className={inputClass}>
            {EXPENSE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="incurredDate" className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            Date incurred
          </label>
          <input id="incurredDate" type="date" {...register('incurredDate')} className={inputClass} />
          {errors.incurredDate ? <span className="text-xs text-red-400">{errors.incurredDate.message}</span> : null}
        </div>
      </div>
      <div>
        <label htmlFor="description" className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
          Description
        </label>
        <textarea id="description" rows={2} {...register('description')} className={inputClass} />
        {errors.description ? <span className="text-xs text-red-400">{errors.description.message}</span> : null}
      </div>

      <ApiErrorNotice error={mutation.error} fallback="Could not create the claim." />
      {success ? (
        <p role="status" className="text-sm text-green-400">
          Draft claim created — attach a receipt below, then submit it for approval.
        </p>
      ) : null}

      <button
        type="submit"
        disabled={isSubmitting || mutation.isPending}
        className="rounded bg-white text-black text-sm px-4 py-2 font-medium disabled:opacity-50"
      >
        {isSubmitting || mutation.isPending ? 'Creating…' : 'Create draft'}
      </button>
    </form>
  );
}

function ClaimRow({ claim }: { claim: ExpenseClaim }): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['me', 'expense-claims'] });

  const submitMut = useMutation({ mutationFn: () => submitClaim(claim.id), onSuccess: () => void refresh() });
  const cancelMut = useMutation({ mutationFn: () => cancelClaim(claim.id), onSuccess: () => void refresh() });

  const editable = isEditable(claim.status);
  const reimb = reimbursementLabel(claim);

  return (
    <div className="rounded border border-gray-900 p-4 space-y-2">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-white">
            {claim.category} · <span className="font-mono">{formatMoney(claim.amount, claim.currency as CurrencyCode)}</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">Incurred {formatDate(claim.incurredDate)}</p>
          <p className="text-sm text-gray-300 mt-1">{claim.description}</p>
        </div>
        <div className="text-right space-y-1">
          <StatusPill status={claim.status} />
          {reimb ? <p className="text-[0.65rem] text-gray-500">{reimb}</p> : null}
        </div>
      </header>

      {claim.status === 'rejected' && claim.decisionReason ? (
        <p className="text-xs text-red-400">Rejected: {claim.decisionReason}</p>
      ) : null}

      <ClaimReceipts claimId={claim.id} canModify={editable} />

      {editable ? (
        <div className="flex gap-2">
          {claim.status === 'draft' ? (
            <button
              type="button"
              onClick={() => submitMut.mutate()}
              disabled={submitMut.isPending}
              className="rounded bg-green-700 text-white text-xs px-3 py-1 disabled:opacity-50"
            >
              {submitMut.isPending ? 'Submitting…' : 'Submit for approval'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => cancelMut.mutate()}
            disabled={cancelMut.isPending}
            className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900 disabled:opacity-50"
          >
            {cancelMut.isPending ? 'Cancelling…' : 'Cancel'}
          </button>
        </div>
      ) : null}
      <ApiErrorNotice error={submitMut.error ?? cancelMut.error} fallback="Action failed." compact />
    </div>
  );
}

export default MyExpenseClaimsPage;
